import { CloudOff, Inbox, MobileOff, Refresh } from '@mui/icons-material';
import { Box, Button, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import messages from '../../intl/messages';
import { fetchMobileCameraLog } from '../../store/model/actions';
import { getCurrentExam, getMobileCameraLog } from '../../store/model/selectors';
import {
	MOBILE_LOG_SEVERITY,
	buildMarkerEntries,
	buildMilestones,
	buildVerdict,
	deriveIntervals,
	extractQueueDepthSeries,
} from '../../utils/mobileCameraLog';
import LogEventList from '../MobileCameraLog/LogEventList';
import LogRawData from '../MobileCameraLog/LogRawData';
import LogSummary from '../MobileCameraLog/LogSummary';
import LogTimeline from '../MobileCameraLog/LogTimeline';
import SaveCloseModalTemplate from './SaveCloseModalTemplate';

const EMPTY_ENTRIES = []; // stable reference so entries-derived useMemo hooks below don't recompute on every render

const EmptyState = ({ icon, title, description, action }) => (
	<Box sx={{ minHeight: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 1 }}>
		{icon}
		<Typography sx={{ fontSize: 16, fontWeight: 600 }}>{title}</Typography>
		{description && <Typography sx={{ color: 'text.secondary', maxWidth: 480 }}>{description}</Typography>}
		{action}
	</Box>
);

const LoadingState = () => (
	<Box sx={{ minHeight: 300 }}>
		<Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
		<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
			{Array.from({ length: 6 }).map((_, i) => (
				<Skeleton key={i} variant="rounded" width={96} height={60} />
			))}
		</Box>
		<Skeleton variant="rounded" height={180} sx={{ mb: 2 }} />
		{Array.from({ length: 6 }).map((_, i) => (
			<Skeleton key={i} variant="text" height={32} />
		))}
	</Box>
);

const MobileCameraLogModal = ({ examId }) => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const exam = useSelector(getCurrentExam);
	const log = useSelector(getMobileCameraLog(examId));
	const [status, setStatus] = useState(() => (log ? 'ready' : 'loading'));
	const [fetchError, setFetchError] = useState(null);
	const [selectedRange, setSelectedRange] = useState(null);
	const [severityFilter, setSeverityFilter] = useState([]);

	const load = () => {
		setStatus('loading');
		setFetchError(null);
		dispatch(fetchMobileCameraLog(examId)).then((result) => {
			if (result?.ok) setStatus('ready');
			else {
				setFetchError(result?.message);
				setStatus('error');
			}
		});
	};

	useEffect(() => {
		if (log) {
			setStatus('ready');
			return;
		}
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [examId]);

	const entries = log?.entries ?? EMPTY_ENTRIES;
	const summary = log?.summary;

	const intervals = useMemo(() => deriveIntervals(entries), [entries]);
	const markerEntries = useMemo(() => buildMarkerEntries(entries), [entries]);
	const queueSeries = useMemo(() => extractQueueDepthSeries(entries), [entries]);
	const milestones = useMemo(() => buildMilestones(entries), [entries]);
	const verdict = useMemo(() => buildVerdict(summary, intervals, exam), [summary, intervals, exam]);

	// default severity filter (Chyby+Varování when problems exist) is only decided once, when data first arrives
	useEffect(() => {
		if (!summary) return;
		setSeverityFilter(
			(summary.severityCounts?.error ?? 0) > 0 || (summary.severityCounts?.warning ?? 0) > 0
				? [MOBILE_LOG_SEVERITY.ERROR, MOBILE_LOG_SEVERITY.WARNING]
				: []
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [log]);

	const seek = (range) => setSelectedRange(range);
	const seekMilestone = (entry) => seek({ start: Math.max(0, entry.offsetMs - 1000), end: entry.offsetMs + 1000 });
	const seekSeverity = (severity) => setSeverityFilter((prev) => (prev.includes(severity) ? prev : [...prev, severity]));

	const title = (
		<Box sx={{ display: 'flex', alignItems: 'center' }}>
			<Box component="span">{intl.formatMessage(messages.mobileLogTitle)}</Box>
			{exam && (
				<Typography component="span" sx={{ ml: 1, fontSize: 14, color: 'text.secondary', fontWeight: 400 }}>
					{exam.firstName} {exam.lastName}
				</Typography>
			)}
			{/* cache in Redux is never invalidated on its own (e.g. a still-running exam), so reopening the
			modal alone won't show new data - this is available regardless of status, not just on error */}
			<Tooltip title={intl.formatMessage(messages.mobileLogRefresh)}>
				<span>
					<IconButton size="small" onClick={load} disabled={status === 'loading'} sx={{ ml: 1 }}>
						<Refresh fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);

	let content;
	if (status === 'loading') {
		content = <LoadingState />;
	} else if (status === 'error') {
		content = (
			<EmptyState
				icon={<CloudOff sx={{ fontSize: 48, color: 'text.disabled' }} />}
				title={intl.formatMessage(messages.mobileLogFetchFailedTitle)}
				description={fetchError}
				action={
					<Button variant="contained" onClick={load}>
						<FormattedMessage {...messages.mobileLogRetry} />
					</Button>
				}
			/>
		);
	} else if (!log?.exists) {
		content = (
			<EmptyState
				icon={<MobileOff sx={{ fontSize: 48, color: 'text.disabled' }} />}
				title={intl.formatMessage(messages.mobileLogNotConnectedTitle)}
				description={intl.formatMessage(messages.mobileLogNotConnectedDesc)}
			/>
		);
	} else if (!entries.length) {
		content = (
			<EmptyState
				icon={<Inbox sx={{ fontSize: 48, color: 'text.disabled' }} />}
				title={intl.formatMessage(messages.mobileLogEmptyTitle)}
				description={
					<>
						{intl.formatMessage(messages.mobileLogEmptyDesc)}
						{summary?.malformedLines > 0 && (
							<>
								<br />
								<FormattedMessage {...messages.mobileLogParseNote} values={{ duplicates: summary.duplicatesRemoved ?? 0, malformed: summary.malformedLines }} />
							</>
						)}
					</>
				}
			/>
		);
	} else {
		content = (
			<Box sx={{ maxHeight: '82vh', overflowY: 'auto' }}>
				<LogSummary
					summary={summary}
					verdict={verdict}
					milestones={milestones}
					truncated={log?.truncated}
					onSeverityFilterClick={seekSeverity}
					onSeekMilestone={seekMilestone}
				/>
				<Box sx={{ my: 2 }}>
					<LogTimeline
						entries={entries}
						summary={summary}
						intervals={intervals}
						markerEntries={markerEntries}
						queueSeries={queueSeries}
						selectedRange={selectedRange}
						onSelectRange={seek}
					/>
				</Box>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					<FormattedMessage {...messages.mobileLogEvents} />
				</Typography>
				<LogEventList
					entries={entries}
					summary={summary}
					selectedRange={selectedRange}
					onClearSelection={() => setSelectedRange(null)}
					severityFilter={severityFilter}
					setSeverityFilter={setSeverityFilter}
				/>
				<Box sx={{ mt: 2 }}>
					<LogRawData examId={examId} entries={entries} summary={summary} />
				</Box>
			</Box>
		);
	}

	return (
		<SaveCloseModalTemplate title={title} maxWidth="xl" isSaveBtnVisible={false}>
			{content}
		</SaveCloseModalTemplate>
	);
};

MobileCameraLogModal.propTypes = {
	examId: PropTypes.string.isRequired,
};

export default MobileCameraLogModal;
